/**
 * Forgot Password Page (Story 6-6-7)
 * Allows users to request a password reset via email.
 */
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { authService } from "../../services/authService";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/atoms/Button";
import { Alert } from "../../components/molecules/Alert";
import { Card } from "../../components/molecules/Card";
import logoImage from "../../assets/global-clean-logo.png";
import { LanguageSwitcher } from "../../components/molecules/LanguageSwitcher";

interface ForgotPasswordFormData {
  email: string;
}

export function ForgotPassword() {
  const { t } = useTranslation("auth");
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect logged-in users to dashboard (AC 10)
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setError("");
    setIsLoading(true);

    try {
      await authService.requestPasswordReset(data.email);
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("forgot_send_failed");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-900 px-4 relative">
      <div className="absolute top-4 end-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div className="flex flex-col items-center">
          <img
            src={logoImage}
            alt={t("forgot_logo_alt")}
            className="h-24 w-auto mb-4"
          />
          <h2 className="mt-2 text-center text-2xl font-bold text-gray-900 dark:text-neutral-100">
            {t("forgot_title")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-neutral-400">
            {t("forgot_description")}
          </p>
        </div>

        <Card padding="lg">
          {success ? (
            <div className="space-y-4">
              <Alert variant="success">
                {t("forgot_check_email")}
              </Alert>
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                  {t("forgot_no_email")}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setSuccess(false);
                      setError("");
                    }}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {t("forgot_try_again")}
                  </button>
                </p>
                <Link
                  to="/login"
                  className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t("forgot_back_to_login")}
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <Alert variant="danger">
                  <div className="flex items-start justify-between gap-2">
                    <span>{error}</span>
                    <button
                      type="button"
                      onClick={() => setError("")}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex-shrink-0"
                      aria-label={t("forgot_dismiss_error")}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </Alert>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-neutral-300"
                >
                  {t("forgot_email_label")}
                </label>
                <input
                  {...register("email", {
                    required: t("forgot_email_required"),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t("forgot_email_invalid"),
                    },
                  })}
                  type="email"
                  id="email"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                placeholder={t("forgot_email_placeholder")}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
                {isLoading ? t("forgot_sending") : t("forgot_submit")}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t("forgot_back_to_login")}
                </Link>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
